import { createContext, useCallback, useContext, useEffect, useMemo, useState }
  from 'react';
import type { ReactNode } from 'react';
import { startAnalytics, stopAnalytics } from '../lib/analytics';

/**
 * Analytics consent.
 *
 * Three states, and the starting one is not "yes". Until somebody chooses,
 * nothing is loaded and nothing is sent — a banner that measures you while
 * you read it is not a consent mechanism.
 *
 * The choice is recorded with the version of the wording it was given under,
 * which is the same discipline the product already applies to signer consent:
 * when the wording changes materially, the old answer no longer covers the new
 * question and the choice is asked again.
 */
export type ConsentStatus = 'unset' | 'granted' | 'denied';

const KEY = 'esign.consent.analytics';

/** Bump when the wording materially changes. Stored choices under an older
 *  version are treated as unset and asked again. */
export const CONSENT_VERSION = '2026-10-20';

type Stored = { status: ConsentStatus; version: string; at: string };

type ConsentValue = {
  status: ConsentStatus;
  decidedAt: string | null;
  grant: () => void;
  deny: () => void;
  /** Reopen the choice — the withdrawal route, reachable from the footer. */
  reconsider: () => void;
};

const ConsentContext = createContext<ConsentValue | null>(null);

function read(): Stored | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Stored;
    if (parsed.version !== CONSENT_VERSION) return null;
    if (parsed.status !== 'granted' && parsed.status !== 'denied') return null;
    return parsed;
  } catch {
    return null;
  }
}

export function ConsentProvider({ children }: { children: ReactNode }) {
  const [stored, setStored] = useState<Stored | null>(() => read());
  const status: ConsentStatus = stored?.status ?? 'unset';

  const write = useCallback((next: ConsentStatus | null) => {
    try {
      if (next === null) {
        localStorage.removeItem(KEY);
        setStored(null);
        return;
      }
      const record: Stored = {
        status: next, version: CONSENT_VERSION, at: new Date().toISOString(),
      };
      localStorage.setItem(KEY, JSON.stringify(record));
      setStored(record);
    } catch {
      // Storage can be unavailable (private mode, blocked cookies). Treat the
      // choice as made for this tab only; the safe fallback is that a refusal
      // persists in memory and an acceptance is asked for again.
      setStored(next === 'denied'
        ? { status: 'denied', version: CONSENT_VERSION, at: '' }
        : null);
    }
  }, []);

  const grant = useCallback(() => write('granted'), [write]);
  const deny = useCallback(() => write('denied'), [write]);
  const reconsider = useCallback(() => {
    stopAnalytics();
    write(null);
  }, [write]);

  useEffect(() => {
    if (status === 'granted') {
      void startAnalytics();
    } else {
      // Covers withdrawal within a session: whatever was loaded is dropped
      // and nothing further is reported.
      stopAnalytics();
    }
  }, [status]);

  const value = useMemo<ConsentValue>(() => ({
    status, decidedAt: stored?.at || null, grant, deny, reconsider,
  }), [status, stored?.at, grant, deny, reconsider]);

  return (
    <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
  );
}

export function useConsent(): ConsentValue {
  const context = useContext(ConsentContext);
  if (!context) throw new Error('useConsent must be used inside ConsentProvider');
  return context;
}
